import { withDefaults } from "stores/utils/withDefaults";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

// ─── Constants ───────────────────────────────────────────────────────────────
export const DEFAULT_WELL_DETAILS_GROUP = "Header";

// ─── State ───────────────────────────────────────────────────────────────────
// Client-selection slice of the replaced `WellDetailsContext` — `wellDetails`
// (the per-group backend-fetched dict, including the `[0]` PDF-blob slot) is
// backend-sourced and lives in React Query (`useWellDetailGroupQuery.ts`) per
// STATE_OWNERSHIP_CONTRACT.md §3.1/§3.2. `loadedGroups` is dropped entirely —
// its *trigger* role is replaced by the local `requestedUnits` state in
// `useWellDetailsData.tsx`, and its *result-tracking* role is replaced by
// `query.isSuccess` per request (derived, not stored).
export interface WellDetailsState {
  currentGroup: string;
}

// ─── Actions ─────────────────────────────────────────────────────────────────
export interface WellDetailsActions {
  readonly setActiveGroup: (group: string) => void;
  readonly reset: () => void;
}

// ─── Combined store type ─────────────────────────────────────────────────────
export type WellDetailsStore = WellDetailsState & WellDetailsActions;

// ─── Initial state factory ───────────────────────────────────────────────────
const makeWellDetailsDefaults = (): WellDetailsState => ({
  currentGroup: DEFAULT_WELL_DETAILS_GROUP
});

export const createInitialState = withDefaults(makeWellDetailsDefaults);

// ─── Store factory ───────────────────────────────────────────────────────────
export const createWellDetailsStore = (overrides?: Partial<WellDetailsState>) => {
  const initial = createInitialState(overrides);

  return createStore<WellDetailsStore>()(
    subscribeWithSelector((set) => ({
      ...initial,

      setActiveGroup: (group) => set({ currentGroup: group }),

      reset: () => set(createInitialState(overrides))
    }))
  );
};

export type WellDetailsStoreApi = ReturnType<typeof createWellDetailsStore>;
